import { useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import styles from "./OverviewGallery.module.scss";
import OverviewMediaSwitcher from "./components/OverviewMediaSwitcher";
import usePropertyGalleryActions from "./hooks/usePropertyGalleryActions";
import buildOverviewGalleryTiles, { OverviewTile } from "./utils/buildOverviewGalleryTiles";
import { getOverviewGalleryEventProps, trackHeroImageClick } from "./utils/galleryEvents";
import { OVERVIEW_NEW_FOLD_MEDIA, OVERVIEW_OLD_FOLD_MEDIA } from "./hooks/useOverviewGalleryArm";
import { Helmet } from "react-helmet";
import getFeaturedImage from "@Utils/getFeaturedImage";
import {
  getVideoThumb,
  hasVideoElement,
  hasVirtualViewsElement,
} from "../SectionFeatured.tsx/utils";
import Image from "@Components/Image";
import Skeleton from "@Components/Skeleton";
import Overview360Icon from "@Icons/Overview360Icon";
import VideoPlayIcon from "@Icons/VideoPlayIcon";
import { AppState } from "@Store/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import getImagePath from "@Utils/getImagePath";
import wrapperHOC from "@Utils/wrapperHOC";
import { PAGE } from "constants/configConstants";
import gtagEventEmitter from "helpers/gtagEventEmitter";

const VIDEO_POSTER_DIMENSIONS = { width: 660, height: 400 };

// Tiles drawn wider than the 920px `gallery-image-medium` covers on a retina screen: the single
// image (case 1), the two halves (case 2) and the wide 196px-high tiles (cases 3 and 4). They are
// requested at 2x their size at 1440 and cropped to the tile, as RoomMediaGallery's
// LimitedMediaLayout does. Keyed by layout case, then tile position.
const RETINA_SCALE = 2;
const SHARP_TILE_SIZES: Record<string, Record<number, { w: number; h: number }>> = {
  "1": { 1: { w: 1328, h: 400 } },
  "2": { 1: { w: 660, h: 400 }, 2: { w: 660, h: 400 } },
  "3": { 2: { w: 660, h: 196 }, 3: { w: 660, h: 196 } },
  "4": { 4: { w: 660, h: 196 } },
};

const getTileImageSrc = (layoutCase: string, position: number, path: string) => {
  const sharpSize = SHARP_TILE_SIZES[layoutCase]?.[position];
  if (sharpSize) {
    return getImagePath(path, "gallery-image-medium", {
      w: sharpSize.w * RETINA_SCALE,
      h: sharpSize.h * RETINA_SCALE,
      fit: "crop",
    });
  }
  // The 660px hero of cases 3–5+ keeps today's first-slide URL, so it is a cache hit when the
  // variant swaps in; grid tiles keep the 600px modifier.
  return position === 1
    ? getImagePath(path, "gallery-image-medium", { q: 70 })
    : getImagePath(path, "gallery-image-small");
};

/**
 * Hero gallery (variant arm only): static featured hero, then exterior · amenities · 360° · video,
 * laid out by media count, with the media switcher on top. Rendered by SectionFeaturedDesktop
 * when useOverviewGalleryArm() === "new".
 */
const OverviewGallery = () => {
  const { data, roomTypesData } = useSelector(
    ({ propertyPageData }: AppState) => ({
      data: propertyPageData.data,
      roomTypesData: propertyPageData.roomTypes?.data || [],
    }),
    shallowEqual,
  );
  const { openGallery, scrollToMap, scrollToFloorPlans } = usePropertyGalleryActions();
  const model = useMemo(
    () => buildOverviewGalleryTiles(data, roomTypesData),
    [data, roomTypesData],
  );
  const imgAltPrefix = `${data?.name} - ${data?.region?.combined_name}`;

  const onTileClick = (tile: OverviewTile) => {
    openGallery(tile.tab);
    // Today's events, kept firing with the arm so existing reports keep counting (E-4).
    gtagEventEmitter({
      ...(tile.tab === "all"
        ? { action: "clicked_photos", label: "photo_section" }
        : { action: "clicked_object", label: tile.tab }),
      category: PAGE.INVENTORY,
      ...getOverviewGalleryEventProps(),
    });
    trackHeroImageClick({
      propertyId: data?.id,
      tilePosition: tile.position,
      tileType: tile.tileType,
      layoutCase: model.layoutCase,
      mediaCount: model.mediaCount,
    });
  };

  if (!model.tiles.length) {
    return <Skeleton width="100%" height="400px" addBorderRadius />;
  }

  const getTileSrc = (tile: OverviewTile) =>
    tile.kind === "video"
      ? getVideoThumb(tile.video, VIDEO_POSTER_DIMENSIONS)
      : getTileImageSrc(model.layoutCase, tile.position, tile.image?.path);

  // Both top folds are in the server HTML and CSS shows one by width (overviewLayout.module.scss),
  // so each hero is preloaded only where its fold shows. Both links are emitted here because
  // react-helmet de-duplicates <link>s by href: when the heroes share a URL (cases 3–5+) one link
  // with no media covers both widths; otherwise the last fold's link would win.
  const newHeroSrc = getTileSrc(model.tiles[0]);
  // Today's first carousel slide (SectionFeaturedDesktop).
  const featuredPath = getFeaturedImage(data?.images)?.path;
  const oldHeroSrc = featuredPath && getImagePath(featuredPath, "gallery-image-medium", { q: 70 });
  const preloads: { href: string; media?: string }[] = (
    newHeroSrc === oldHeroSrc
      ? [{ href: newHeroSrc }]
      : [
          { href: newHeroSrc, media: OVERVIEW_NEW_FOLD_MEDIA },
          { href: oldHeroSrc, media: OVERVIEW_OLD_FOLD_MEDIA },
        ]
  ).filter(({ href }) => href);

  return (
    <div
      className={formatClassNames(styles.gallery, styles[`case${model.tiles.length}`])}
      data-testid="overview-gallery"
    >
      <Helmet>
        {preloads.map(({ href, media }) => (
          <link
            key={href}
            rel="preload"
            as="image"
            fetchPriority="high"
            href={href}
            {...(media ? { media } : {})}
          />
        ))}
      </Helmet>
      {model.tiles.map((tile) => {
        const alt = tile.image?.caption ? `${imgAltPrefix} - ${tile.image.caption}` : imgAltPrefix;
        const src = getTileSrc(tile);
        return (
          <button
            key={`${tile.position}-${tile.kind}`}
            type="button"
            className={formatClassNames(styles.tile, styles[`tile${tile.position}`])}
            onClick={() => onTileClick(tile)}
            aria-label={alt}
            data-testid={`overview-gallery-tile-${tile.position}`}
          >
            <Image
              src={src}
              alt={alt}
              width="100%"
              height="100%"
              className={styles.image}
              // The hero's preload is the media-scoped link above.
              isNotLazy
              dataTestId="propertypage-gallery-thumbnail-image"
            />
            {tile.kind === "360" && (
              <span
                className={formatClassNames(styles.overlay, styles.overlay360)}
                aria-hidden="true"
              >
                <Overview360Icon />
              </span>
            )}
            {tile.kind === "video" && (
              <span
                className={formatClassNames(styles.overlay, styles.overlayVideo)}
                aria-hidden="true"
              >
                <VideoPlayIcon size="40" fill="white" />
              </span>
            )}
          </button>
        );
      })}
      <div className={styles.switcherSlot}>
        <OverviewMediaSwitcher
          counts={model.counts}
          hasVideos={Boolean(hasVideoElement(data))}
          has3D={Boolean(hasVirtualViewsElement(data))}
          propertyId={data?.id}
          onOpenGallery={openGallery}
          onScrollToMap={scrollToMap}
          onScrollToFloorPlans={scrollToFloorPlans}
        />
      </div>
    </div>
  );
};

export default wrapperHOC(OverviewGallery, {
  componentName: "OverviewGallery-PropertyPage",
  showForChina: false,
  showForWhiteLable: false,
  height: "400px",
});
